import cn from 'classnames';
import differenceInDays from 'date-fns/differenceInDays';
import { useRouter } from 'next/router';
import React, { memo, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Icon, IconButton } from 'components';
import AutoComplete from 'components/AutoComplete';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import { Form } from 'components/form';
import { Col, Row } from 'components/layout';

import { useEvent } from 'hooks/useEvent';
import { useNotification } from 'hooks/useNotification';

import { PlaceAttributes } from 'lib/models/event';
import { OpportunityAttributes } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';

import { fetchAddressComponents } from 'utils/map';
import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from '../PendingNotification.module.scss';

export interface PendingLocationNotificationProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  touchpoint: OpportunityAttributes;
}

export const PendingLocationNotification: React.FC<PendingLocationNotificationProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      touchpoint,
    }: PendingLocationNotificationProps) => {
      const router = useRouter();
      const notificationInstance = useNotification();
      const [isModalOpen, setModalOpen] = useState(false);
      const { id, title, start_date, creatable_for } = touchpoint;
      const { onAddEventLocation } = useEvent();
      const { mutateTouchpoint } = useAttendeesFilter();
      const [placeAttributes, setPlaceAttributes] =
        useState<PlaceAttributes | null>(null);
      const [showFurtherLocationDetailBox, setShowFurtherLocationDetailBox] =
        useState(false);
      const isSocietyEvent = useMemo(() => {
        return creatable_for?.data.type === 'student_society';
      }, [creatable_for]);

      const [t] = useTranslation('event-dashboard');

      const editLink = useMemo(() => {
        return `${(isSocietyEvent
          ? PAGE_ROUTES.EVENTS_EDIT
          : PAGE_ROUTES.CAMPAIGNS_EDIT
        ).replace('[id]', id as string)}?step=1&type=${TouchpointType.Event}`;
      }, [id, isSocietyEvent]);

      const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
      } = useForm();

      const onSubmit: SubmitHandler<{ address_details: string }> = async (
        data
      ) => {
        if (placeAttributes) {
          const { address_details } = data;
          const response = await onAddEventLocation({
            id,
            place_attributes: {
              ...placeAttributes,
              address_details,
            },
          });
          if (response) {
            mutateTouchpoint();
            setModalOpen(false);
            notificationInstance.success({
              title: t('location.added.title'),
              message: t('location.added.message'),
            });
          }
        }
      };

      const diff = differenceInDays(new Date(start_date), new Date());

      return (
        <div
          className={cn(
            {
              [styles.PendingNotification]: true,
            },
            { [styles.PendingNotificationWarning]: diff < 3 },
            className
          )}
          style={style}
          data-testid="PendingLocationNotification"
        >
          <Row align="start" wrap={false} className={styles.header}>
            <Col>
              <Icon iconName="icon_info" size="large" />
            </Col>
            <Col>
              <p
                className={cn(styles.title, {
                  [styles.titleWarning]: diff < 3,
                })}
              >
                {t('location.title')}
              </p>
              {diff < 3 ? (
                <p className={cn(styles.desc, styles.descWarning)}>
                  <mark>{t('location.message-1-mark', { title })}</mark>{' '}
                  {t('location.message-1')}
                </p>
              ) : (
                <p className={styles.desc}>{t('location.message-2')}</p>
              )}
            </Col>
          </Row>
          <ButtonV3 onClick={() => setModalOpen(true)} color="secondary">
            {t('location.button')}
          </ButtonV3>
          {isModalOpen ? (
            <Modal
              isOpen={isModalOpen}
              onRequestClose={() => setModalOpen(false)}
              className={styles.modal}
              contentClassName={styles.modalContent}
            >
              <IconButton
                size="xsmall"
                iconName="close"
                className={styles.cancel}
                onClick={() => setModalOpen(false)}
              />
              <Form
                autoComplete="off"
                onSubmit={handleSubmit(onSubmit)}
                style={style}
                data-testid="PendingLocationNotification"
              >
                <div className={styles.header}>
                  <p className={styles.title}> {t('location.modal.title')}</p>
                  <p className={styles.desc}>{t('location.modal.message')}</p>
                  <AutoComplete
                    placeAttributes={placeAttributes}
                    fetchAddressComponents={fetchAddressComponents}
                    setPlaceAttributes={setPlaceAttributes}
                    v2
                    className={styles.autoComplete}
                    labelClassName={styles.inputLabel}
                  />
                  {showFurtherLocationDetailBox ? (
                    <>
                      <InputV2
                        label={t('location.modal.input.label')}
                        placeholder={t('location.modal.input.placeholder')}
                        {...register('address_details')}
                        isInvalid={!!errors.address_details}
                        helperText={errors.address_details?.message}
                        className={styles.input}
                        labelClassName={styles.inputLabel}
                        infoToolTip={
                          <Tooltip
                            content={t('location.modal.input.tooltip')}
                            position="top"
                            xAlign="center"
                            color="dark"
                            className={cn(styles.tooltipContainer)}
                          >
                            <Icon iconName="icon_info" size="xsmall" />
                          </Tooltip>
                        }
                      />
                    </>
                  ) : (
                    <ButtonV3
                      type="button"
                      color="secondary"
                      size="small"
                      onClick={() => setShowFurtherLocationDetailBox(true)}
                      className={cn(styles.button, {
                        [styles.hide]: showFurtherLocationDetailBox,
                      })}
                      startIcon="icon_plus"
                    >
                      {t('location.modal.input.button-to-add')}
                    </ButtonV3>
                  )}
                </div>
                <div className={styles.separator}></div>
                <Row align="center" justify="end" className={styles.buttons}>
                  <ButtonV3
                    type="button"
                    color="secondary"
                    borderless
                    onClick={() => {
                      router.push(editLink);
                    }}
                  >
                    {t('location.modal.button-1')}
                  </ButtonV3>
                  <ButtonV3
                    type="submit"
                    disabled={!placeAttributes || isSubmitting}
                    isLoading={isSubmitting}
                    borderless
                  >
                    {t('location.modal.button-2')}
                  </ButtonV3>
                </Row>
              </Form>
            </Modal>
          ) : null}
        </div>
      );
    }
  );

PendingLocationNotification.displayName = 'PendingLocationNotification';
